import { gql, QueryHookOptions, useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { prop, sortBy } from 'remeda';
import { type IconLibrary } from 'src/components/DynamicIcon/dynamic-icon';
import { Records } from 'src/graphql/types';
import type { ConversationUrgencyRaw } from 'src/types';

const GET_ENABLED_CONVERSATION_URGENCIES_QUERY = gql`
  query GetEnabledConversationUrgencies($partnerId: Int) @entity(name: "ConversationUrgency") {
    getEnabledConversationUrgencies(partnerId: $partnerId) {
      records {
        id
        urgencyName
        sortOrder
      }
    }
  }
`;

interface GetEnabledConversationUrgenciesQuery {
  getEnabledConversationUrgencies: Records<ConversationUrgencyRaw>;
}

export interface ConversationUrgency {
  id: BaseConversationUrgency | string;
  name: ConversationUrgencyRaw['urgencyName'];
  sortOrder: number;
}

export const useConversationUrgencyList = (
  partnerId?: number,
  options?: QueryHookOptions<GetEnabledConversationUrgenciesQuery>,
) => {
  const { data, ...rest } = useQuery<GetEnabledConversationUrgenciesQuery>(
    GET_ENABLED_CONVERSATION_URGENCIES_QUERY,
    {
      ...options,
      context: {
        omitIsolationModeHeader: true,
      },
      variables: {
        partnerId,
      },
    },
  );
  const conversationUrgencyList = useMemo(() => {
    if (!data?.getEnabledConversationUrgencies?.records?.length) {
      return null;
    }

    return data?.getEnabledConversationUrgencies.records.map(
      (conversationUrgency: ConversationUrgencyRaw) => {
        return {
          id: conversationUrgency.id,
          name: conversationUrgency.urgencyName,
          sortOrder: conversationUrgency.sortOrder,
        };
      },
    );
  }, [data?.getEnabledConversationUrgencies.records]);

  return {
    conversationUrgencyList,
    ...rest,
  };
};
export function useConversationUrgencyOptions(
  partnerId?: number,
  options?: QueryHookOptions<GetEnabledConversationUrgenciesQuery>,
) {
  const { conversationUrgencyList } = useConversationUrgencyList(partnerId, options);
  return useMemo(() => {
    if (!conversationUrgencyList) return [];
    return sortBy(
      conversationUrgencyList.map((item) => {
        const display = getUrgencyDisplay(item.id);
        return {
          id: item.id,
          value: item.id,
          label: item.name,
          sortOrder: item.sortOrder,
          icon: display.icon,
          iconLibrary: display.library,
          color: display.color,
        };
      }),
      prop('sortOrder'),
    );
  }, [conversationUrgencyList]);
}

export enum BaseConversationUrgency {
  Low = 'low',
  Normal = 'normal',
  Urgent = 'urgent',
  LifeSafety = 'life_safety',
}

export type UrgencyDisplayProps = {
  icon: string;
  library: IconLibrary;
  color: string;
};

export function getUrgencyDisplay<T extends string>(urgency: T): UrgencyDisplayProps {
  switch (urgency) {
    case BaseConversationUrgency.Low:
      return { icon: 'signal-bars-1', library: 'custom', color: 'grey.500' };
    case BaseConversationUrgency.Normal:
      return {
        icon: 'signal-bars-3',
        library: 'custom',
        color: 'custom.tertiary.tertiary60',
      };
    case BaseConversationUrgency.Urgent:
      return { icon: 'triangle-alert', library: 'lucide', color: 'warning.main' };
    case BaseConversationUrgency.LifeSafety:
      return { icon: 'siren', library: 'lucide', color: 'error.main' };
    default:
      return { icon: 'signal-bars-3', library: 'custom', color: 'text.secondary' };
  }
}

export function getUrgencyIcon<T extends string>(urgency: T): string {
  return getUrgencyDisplay(urgency).icon;
}

export function getUrgencyColor<T extends string>(urgency: T): string {
  return getUrgencyDisplay(urgency).color;
}
